import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import {
  CheckCircle,
  Trophy,
  Clock,
  Target,
  Star,
  ArrowRight,
  RotateCcw,
  Home
} from 'lucide-react'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import Badge from '../components/ui/Badge'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import { StudySessionResult } from '../types/flashcard'

interface SessionStats {
  cardsStudied: number
  cardsCorrect: number
  cardsIncorrect: number
  duration: number
  score: number
  xpEarned: number
  badgesEarned: string[]
}

const SessionComplete: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  
  const [sessionStats, setSessionStats] = useState<SessionStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadSessionData = async () => {
      try {
        // location.state'ten sessionResult'ı al
        const sessionResult = location.state?.sessionResult as StudySessionResult
        
        if (sessionResult) {
          // location.state'ten gelen veriyi kullan
          const stats: SessionStats = {
            cardsStudied: sessionResult.cardsStudied,
            cardsCorrect: sessionResult.cardsCorrect,
            cardsIncorrect: sessionResult.cardsIncorrect,
            duration: sessionResult.duration,
            score: sessionResult.score,
            xpEarned: sessionResult.xpEarned,
            badgesEarned: sessionResult.badgesEarned || []
          }
          setSessionStats(stats)
        } else if (sessionId && sessionId !== 'local' && sessionId !== 'review') {
          // Gerçek sessionId varsa Firebase'den getir (şimdilik hata ver)
          setError('Firebase oturum verileri henüz desteklenmiyor.')
        } else {
          setError('Oturum verileri bulunamadı. Lütfen tekrar çalışma yapın.')
        }
      } catch (err) {
        console.error('Oturum verileri yüklenirken hata:', err)
        setError('Oturum verileri yüklenirken bir hata oluştu.')
      } finally {
        setLoading(false)
      }
    }

    loadSessionData()
  }, [sessionId, location.state])

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const getScoreColor = (score: number): string => {
    if (score >= 90) return 'text-green-600'
    if (score >= 70) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getScoreMessage = (score: number): string => {
    if (score >= 90) return 'Mükemmel! 🎉'
    if (score >= 80) return 'Çok İyi! 👏'
    if (score >= 70) return 'İyi! 👍'
    if (score >= 60) return 'Fena Değil 🙂'
    return 'Daha Çok Çalışmalısın 💪'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-transparent p-6 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-300 font-medium">Sonuçlar yükleniyor...</p>
        </div>
      </div>
    )
  }

  if (error || !sessionStats) {
    return (
      <div className="min-h-screen bg-transparent p-6 flex items-center justify-center">
        <Card className="p-8 text-center max-w-md bg-white/70 dark:bg-gray-800/70 backdrop-blur-md border border-white/20 dark:border-gray-700/30 shadow-xl">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Target className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            {error || 'Oturum sonuçları yüklenemedi'}
          </h2>
          <Button 
            onClick={() => navigate('/flashcards')}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <Home className="w-5 h-5 mr-2" />
            Flashcard'lara Dön
          </Button>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-transparent p-4 sm:p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="relative">
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-green-400 to-emerald-500 dark:from-green-500 dark:to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl animate-pulse">
              <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
            </div>
            <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-400 dark:bg-yellow-500 rounded-full flex items-center justify-center animate-bounce">
              <Trophy className="w-4 h-4 text-yellow-800 dark:text-yellow-900" />
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-purple-800 dark:from-white dark:via-blue-200 dark:to-purple-200 bg-clip-text text-transparent mb-4">
            Çalışma Tamamlandı!
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 font-medium">
            {getScoreMessage(sessionStats.score)}
          </p>
        </div>

        {/* Main Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8 sm:mb-12">
          <Card className="p-4 sm:p-6 text-center bg-white/70 dark:bg-gray-800/70 backdrop-blur-md border border-white/20 dark:border-gray-700/30 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-blue-400 to-blue-600 dark:from-blue-500 dark:to-blue-700 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
              <Target className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
            </div>
            <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-1">
              {sessionStats.cardsStudied}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 font-medium">Çalışılan Kart</div>
          </Card>

          <Card className="p-4 sm:p-6 text-center bg-white/70 dark:bg-gray-800/70 backdrop-blur-md border border-white/20 dark:border-gray-700/30 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-green-400 to-green-600 dark:from-green-500 dark:to-green-700 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
              <CheckCircle className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
            </div>
            <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-green-600 dark:text-green-400 mb-1">
              {sessionStats.cardsCorrect}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 font-medium">Doğru Cevap</div>
          </Card>

          <Card className="p-4 sm:p-6 text-center bg-white/70 dark:bg-gray-800/70 backdrop-blur-md border border-white/20 dark:border-gray-700/30 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-yellow-400 to-orange-500 dark:from-yellow-500 dark:to-orange-600 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
              <Clock className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
            </div>
            <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-1">
              {formatDuration(sessionStats.duration)}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 font-medium">Süre</div>
          </Card>

          <Card className="p-4 sm:p-6 text-center bg-white/70 dark:bg-gray-800/70 backdrop-blur-md border border-white/20 dark:border-gray-700/30 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-purple-400 to-purple-600 dark:from-purple-500 dark:to-purple-700 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
              <Star className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
            </div>
            <div className={`text-xl sm:text-2xl lg:text-3xl font-bold mb-1 ${getScoreColor(sessionStats.score)}`}>
              {sessionStats.score}%
            </div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 font-medium">Başarı Oranı</div>
          </Card>
        </div>

        {/* Detailed Results */}
        <Card className="p-6 sm:p-8 mb-8 sm:mb-12 bg-white/70 dark:bg-gray-800/70 backdrop-blur-md border border-white/20 dark:border-gray-700/30 shadow-xl">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6 sm:mb-8 flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Target className="w-4 h-4 text-white" />
            </div>
            Detaylı Sonuçlar
          </h2>
          
          <div className="space-y-6">
            {/* Score Bar */}
            <div className="bg-gray-50/50 dark:bg-gray-700/50 p-4 sm:p-6 rounded-xl border border-gray-200/50 dark:border-gray-600/50">
              <div className="flex justify-between items-center mb-4">
                <span className="text-sm sm:text-base font-semibold text-gray-700 dark:text-gray-300">Başarı Oranı</span>
                <span className={`text-lg sm:text-xl font-bold ${getScoreColor(sessionStats.score)}`}>
                  {sessionStats.score}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-4 overflow-hidden shadow-inner">
                <div 
                  className={`h-4 rounded-full transition-all duration-1000 ease-out shadow-lg ${
                    sessionStats.score >= 80 ? 'bg-gradient-to-r from-green-400 to-green-600' : 
                    sessionStats.score >= 60 ? 'bg-gradient-to-r from-yellow-400 to-orange-500' : 'bg-gradient-to-r from-red-400 to-red-600'
                  }`}
                  style={{ width: `${sessionStats.score}%` }}
                ></div>
              </div>
            </div>

            {/* XP Earned */}
            {sessionStats.xpEarned > 0 && (
              <div className="flex items-center justify-between p-4 sm:p-6 bg-gradient-to-r from-yellow-50/80 to-orange-50/80 dark:from-yellow-900/30 dark:to-orange-900/30 rounded-xl border border-yellow-200/50 dark:border-yellow-700/50 shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                    <Trophy className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 dark:text-white text-base sm:text-lg">XP Kazandın!</div>
                    <div className="text-sm sm:text-base text-gray-600 dark:text-gray-300">Harika performans</div>
                  </div>
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-yellow-600 dark:text-yellow-400">
                  +{sessionStats.xpEarned}
                </div>
              </div>
            )}

            {/* Badges Earned */}
            {sessionStats.badgesEarned.length > 0 && (
              <div className="p-4 sm:p-6 bg-gradient-to-r from-purple-50/80 to-indigo-50/80 dark:from-purple-900/30 dark:to-indigo-900/30 rounded-xl border border-purple-200/50 dark:border-purple-700/50 shadow-lg">
                <h3 className="font-bold text-gray-900 dark:text-white mb-4 text-base sm:text-lg flex items-center gap-2">
                  <Star className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  Kazanılan Rozetler
                </h3>
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  {sessionStats.badgesEarned.map((badge, index) => (
                    <Badge key={index} className="bg-gradient-to-r from-purple-100 to-indigo-100 dark:from-purple-800 dark:to-indigo-800 text-purple-800 dark:text-purple-200 border border-purple-200 dark:border-purple-600 shadow-md">
                      {badge}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center">
          <Button
            onClick={() => navigate('/flashcards')}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 dark:from-blue-500 dark:to-indigo-500 dark:hover:from-blue-600 dark:hover:to-indigo-600 text-white px-8 py-4 text-base font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 w-full sm:w-auto backdrop-blur-sm border border-white/20"
          >
            <Home className="w-5 h-5 mr-3" />
            Ana Sayfaya Dön
          </Button>
          
          <Button
            variant="outline"
            onClick={() => window.history.back()}
            className="px-8 py-4 text-base font-semibold bg-white/50 dark:bg-gray-800/50 backdrop-blur-md border-2 border-gray-300/30 dark:border-gray-600/30 text-gray-700 dark:text-gray-200 hover:bg-white/70 dark:hover:bg-gray-700/70 hover:border-gray-400 dark:hover:border-gray-500 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 w-full sm:w-auto"
          >
            <RotateCcw className="w-5 h-5 mr-3" />
            Tekrar Çalış
          </Button>
        </div>
      </div>
    </div>
  )
}

export default SessionComplete